import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Download, Maximize, Minimize, Minus, Plus, RotateCcw, Scan } from 'lucide-react';
import { api } from '../api.js';
import { mediaVariantUrl, useMediaRestore } from '../hooks/useMediaRestore.js';
import { useCatalogNavigation } from '../hooks/useCatalogNavigation.js';

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

export default function ReaderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shellRef = useRef(null);
  const canvasRef = useRef(null);
  const activeThumbnailRef = useRef(null);
  const mediaRevision = useMediaRestore();
  const [book, setBook] = useState(null);
  const navigation = useCatalogNavigation(book?.itemType);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [imageReady, setImageReady] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageRetry, setImageRetry] = useState(0);
  const [showPageStrip, setShowPageStrip] = useState(() => window.matchMedia('(min-width: 601px)').matches);

  useEffect(() => {
    document.documentElement.classList.add('reader-mode');
    document.body.classList.add('reader-mode');
    return () => {
      document.documentElement.classList.remove('reader-mode');
      document.body.classList.remove('reader-mode');
    };
  }, []);

  useEffect(() => {
    api(`/api/books/${id}`).then((data) => { setBook(data); setPage(clamp((Number(searchParams.get('page')) || 1) - 1, 0, Math.max(0, data.pageUrls.length - 1))); setZoom(1); setRotation(0); setError(''); }).catch((requestError) => setError(requestError.message));
  }, [id]);

  useEffect(() => {
    if (!canvasRef.current) return undefined;
    const updateViewport = () => setViewport({ width: canvasRef.current.clientWidth, height: canvasRef.current.clientHeight });
    updateViewport();
    const observer = new ResizeObserver(updateViewport);
    observer.observe(canvasRef.current);
    return () => observer.disconnect();
  }, [book]);

  useEffect(() => {
    const handleFullscreen = () => setFullscreen(document.fullscreenElement === shellRef.current);
    document.addEventListener('fullscreenchange', handleFullscreen);
    return () => document.removeEventListener('fullscreenchange', handleFullscreen);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 601px)');
    const update = () => setShowPageStrip(mediaQuery.matches);
    mediaQuery.addEventListener?.('change', update);
    return () => mediaQuery.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    activeThumbnailRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [page]);

  useEffect(() => {
    if (!book) return;
    [book.pageUrls[page - 1], book.pageUrls[page + 1]].filter(Boolean).forEach((url) => {
      const image = new Image();
      image.src = mediaVariantUrl(url, 'reader-mobile', mediaRevision);
      image.srcset = `${mediaVariantUrl(url, 'reader-mobile', mediaRevision)} 1400w, ${mediaVariantUrl(url, 'reader', mediaRevision)} 2400w`;
      image.sizes = window.innerWidth < 700 ? '100vw' : '1200px';
    });
  }, [book, page, mediaRevision]);

  useEffect(() => {
    if (!book) return;
    setImageReady(false);
    setImageError(false);
  }, [book, page, mediaRevision, imageRetry]);

  const pageCount = book?.pageUrls?.length || 0;
  const isPostcard = book?.itemType === 'postcard';
  const pageLabel = (index) => book?.pageLabels?.[index] || `Pagina ${index + 1}`;
  const changePage = (nextPage) => {
    if (!pageCount) return;
    const safePage = clamp(nextPage, 0, pageCount - 1);
    if (safePage === page) return;
    setImageReady(false);
    setImageError(false);
    setImageSize({ width: 0, height: 0 });
    setPage(safePage);
    canvasRef.current?.scrollTo({ top: 0, left: 0 });
  };
  const changeZoom = (amount) => setZoom((value) => Number(clamp(value + amount, .5, 3).toFixed(2)));
  const fitPage = () => { setZoom(1); setRotation(0); canvasRef.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' }); };
  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) await shellRef.current?.requestFullscreen();
    else await document.exitFullscreen();
  };
  const goBack = () => navigate(`/carte/${id}`, { state: navigation.state });

  useEffect(() => {
    const handleKeyboard = (event) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target?.isContentEditable) return;
      if (event.key === 'ArrowLeft') changePage(page - 1);
      if (event.key === 'ArrowRight') changePage(page + 1);
      if (event.key === '+' || event.key === '=') changeZoom(.15);
      if (event.key === '-') changeZoom(-.15);
      if (event.key === '0') fitPage();
    };
    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  });

  const fittedPage = useMemo(() => {
    if (!viewport.width || !viewport.height || !imageSize.width || !imageSize.height) return null;
    const padding = viewport.width < 700 ? 28 : 64;
    const availableWidth = Math.max(180, viewport.width - padding);
    const availableHeight = Math.max(220, viewport.height - padding);
    const rotated = rotation % 180 !== 0;
    const visualWidth = rotated ? imageSize.height : imageSize.width;
    const visualHeight = rotated ? imageSize.width : imageSize.height;
    const fitScale = Math.min(availableWidth / visualWidth, availableHeight / visualHeight);
    return {
      stageWidth: Math.round(visualWidth * fitScale * zoom),
      stageHeight: Math.round(visualHeight * fitScale * zoom),
      imageWidth: Math.round(imageSize.width * fitScale * zoom),
      imageHeight: Math.round(imageSize.height * fitScale * zoom)
    };
  }, [viewport, imageSize, zoom, rotation]);

  const handleWheel = (event) => {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    changeZoom(event.deltaY < 0 ? .15 : -.15);
  };

  const activeMediaRevision = mediaRevision + imageRetry;
  const currentPageUrl = book?.pageUrls?.[page] || '';
  const mobilePageUrl = mediaVariantUrl(currentPageUrl, 'reader-mobile', activeMediaRevision);
  const fullPageUrl = mediaVariantUrl(currentPageUrl, 'reader', activeMediaRevision);

  if (error) return <section className="reader-shell jpeg-reader"><div className="reader-loading"><strong>Cartea nu poate fi deschisă</strong><span>{error}</span><Link className="button secondary" to="/"><ArrowLeft size={17}/> Înapoi la catalog</Link></div></section>;

  return <section className="reader-shell jpeg-reader" ref={shellRef}>
    <header className="reader-toolbar glass">
      <button type="button" className="reader-back" onClick={goBack} aria-label="Înapoi la pagina cărții"><ArrowLeft size={19}/><span>Înapoi</span></button>
      <div className="reader-title"><strong>{book?.title || 'Se încarcă…'}</strong><small>{book ? `${pageLabel(page)} · ${page + 1} din ${pageCount}` : 'Pregătim documentul'}</small></div>
      <div className="reader-actions">
        <div className="zoom-controls" aria-label="Control zoom">
          <button type="button" onClick={() => changeZoom(-.15)} disabled={zoom <= .5} title="Micșorează (–)" aria-label="Micșorează"><Minus size={20}/></button>
          <output aria-live="polite">{Math.round(zoom * 100)}%</output>
          <button type="button" onClick={() => changeZoom(.15)} disabled={zoom >= 3} title="Mărește (+)" aria-label="Mărește"><Plus size={20}/></button>
        </div>
        <button type="button" className="reader-tool fit-tool" onClick={fitPage} title="Încadrează pagina (0)"><Scan size={19}/><span>Încadrează</span></button>
        <button type="button" className="reader-tool" onClick={() => setRotation((value) => (value + 90) % 360)} title="Rotește pagina"><RotateCcw size={19}/><span>Rotește</span></button>
        {book?.downloadUrl && <a className="reader-tool" href={book.downloadUrl} title="Descarcă PDF"><Download size={19}/><span>PDF</span></a>}
        <button type="button" className="reader-tool" onClick={toggleFullscreen} title="Ecran complet">{fullscreen ? <Minimize size={19}/> : <Maximize size={19}/>}<span>{fullscreen ? 'Ieșire' : 'Ecran complet'}</span></button>
      </div>
    </header>

    {book ? <div className="reader-workspace">
      {showPageStrip && <aside className="page-strip" aria-label="Pagini">
        <div className="page-strip-heading"><strong>{isPostcard ? 'Față / verso' : 'Pagini'}</strong><span>{pageCount}</span></div>
        {book.pageUrls.map((url, index) => <button ref={index === page ? activeThumbnailRef : null} key={url} className={index === page ? 'active' : ''} aria-current={index === page ? 'page' : undefined} aria-label={`Deschide ${pageLabel(index)}`} onClick={() => changePage(index)}><LazyPageThumbnail src={mediaVariantUrl(url, 'thumb', mediaRevision)}/><span>{isPostcard ? pageLabel(index) : index + 1}</span></button>)}
      </aside>}

      <main className="reader-canvas" ref={canvasRef} onWheel={handleWheel} onDoubleClick={() => setZoom((value) => value === 1 ? 1.6 : 1)} title="Dublu clic pentru zoom; Ctrl + rotiță pentru mărire">
        {!imageReady && !imageError && pageCount > 0 && <span className="spinner reader-page-spinner"/>}
        {imageError && <div className="reader-image-error"><strong>Pagina nu s-a încărcat</strong><span>Conexiunea poate fi lentă sau sesiunea a fost restaurată de Safari.</span><button type="button" className="button secondary" onClick={() => setImageRetry((value) => value + 1)}>Reîncarcă pagina</button></div>}
        {pageCount > 0 ? <div className={`reader-page-stage ${imageReady ? 'ready' : ''}`} style={fittedPage ? { width: fittedPage.stageWidth, height: fittedPage.stageHeight } : undefined}>
          <img className="reader-page-image" src={mobilePageUrl} srcSet={`${mobilePageUrl} 1400w, ${fullPageUrl} 2400w`} sizes={zoom > 1 ? `${Math.min(300, Math.round(100 * zoom))}vw` : '(max-width: 700px) 100vw, 1200px'} decoding="async" alt={`${pageLabel(page)} — ${book.title}`} onLoad={(event) => { setImageSize({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight }); setImageError(false); setImageReady(true); }} onError={() => { setImageReady(false); setImageError(true); }} style={fittedPage ? { width: fittedPage.imageWidth, height: fittedPage.imageHeight, transform: `translate(-50%, -50%) rotate(${rotation}deg)` } : undefined}/>
        </div> : <div className="reader-loading">Acest volum nu are pagini JPEG.</div>}
      </main>

      <nav className="page-navigation" aria-label="Navigare între pagini">
        <button type="button" disabled={page === 0} onClick={() => changePage(page - 1)}><ChevronLeft/><span>{isPostcard ? 'Fața anterioară' : 'Pagina anterioară'}</span></button>
        {isPostcard ? <div className="reader-side-switch">{book.pageUrls.map((_, i) => <button key={i} aria-pressed={page === i} className={page === i ? 'active' : ''} onClick={() => changePage(i)}>{pageLabel(i)}</button>)}</div> : <label className="page-jump"><span className="sr-only">Mergi la pagina</span><input key={page} type="number" min="1" max={pageCount} defaultValue={page + 1} onBlur={(event) => changePage(Number(event.target.value || 1) - 1)} onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}/><small>din {pageCount}</small></label>}
        <button type="button" disabled={page >= pageCount - 1} onClick={() => changePage(page + 1)}><span>{isPostcard ? 'Fața următoare' : 'Pagina următoare'}</span><ChevronRight/></button>
        <span className="reader-shortcuts">Taste: ← → pagini · + – zoom · 0 încadrare</span>
      </nav>
    </div> : <div className="reader-loading"><span className="spinner"/>Pregătim paginile digitizate…</div>}
  </section>;
}

function LazyPageThumbnail({ src }) {
  const imageRef = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const image = imageRef.current;
    if (!image || visible) return undefined;
    if (!('IntersectionObserver' in window)) { setVisible(true); return undefined; }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, { root: image.closest('.page-strip'), rootMargin: '360px 0px' });
    observer.observe(image);
    return () => observer.disconnect();
  }, [visible]);
  return <img ref={imageRef} src={visible ? src : undefined} alt="" decoding="async"/>;
}
